import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikActions, FormikProps } from 'formik';

import { useSnackbarContext } from 'ui-lib/components/Snackbar/SnackbarProvider';
import { requestResetPassword } from 'web-shared/api';
import HttpError from 'web-shared/services/http-service/HttpError';

import {
  ForgotPasswordFormValues,
  forgotPasswordFieldNameGenerator,
  initialValues,
  validationSchema,
} from '../../const';
import { getResetPasswordUrl } from '../../utils';

interface Props {
  children: (props: FormikProps<ForgotPasswordFormValues>) => React.ReactNode;
}

const { n } = forgotPasswordFieldNameGenerator;

const ForgotPasswordPageFormikWrapper = ({ children }: Props) => {
  const { t } = useTranslation();
  const { showSuccessSnackbar, showHttpErrorSnackbar } = useSnackbarContext();

  const handleSubmit = async (
    { email }: ForgotPasswordFormValues,
    { setFieldError, setSubmitting }: FormikActions<ForgotPasswordFormValues>,
  ) => {
    const redirect_url = getResetPasswordUrl(window.location);

    try {
      await requestResetPassword({
        email,
        redirect_url,
      });

      showSuccessSnackbar({
        title: t('auth.title_checkEmail'),
        message: t('auth.description_resetEmailSent', { email }),
      });
    } catch (error) {
      if (HttpError.isHttpError(error)) {
        if (error.status === 403) {
          showSuccessSnackbar({
            title: t('auth.title_checkEmail'),
            message: t('auth.description_resetEmailSent', { email }),
          });
        } else {
          showHttpErrorSnackbar(error);
          setFieldError(n('email'), t('auth.error_userEmailNotFound'));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {children}
    </Formik>
  );
};

export default ForgotPasswordPageFormikWrapper;
